/// <reference types="google.maps" />
import {useQuery} from '@tanstack/react-query'

import {dispatchKeys} from 'src/dispatch/query-keys'
import type {LatLng} from 'src/lib/haversine-miles'

import type {LegTime} from './run-map-logic'

const METERS_PER_MILE = 1609.344
const CONCURRENT_REQUESTS = 4
const ROUTE_MATRIX_FIELDS = ['durationMillis', 'distanceMeters', 'condition']

type LegRequest = {key: string; from: LatLng; to: LatLng}

export type RouteMatrixLike = {
    computeRouteMatrix(request: google.maps.routes.ComputeRouteMatrixRequest): Promise<{
        matrix: {
            rows: {
                items: Pick<
                    google.maps.routes.RouteMatrixItem,
                    'condition' | 'distanceMeters' | 'durationMillis' | 'error'
                >[]
            }[]
        }
    }>
}

/** Leg times by leg key. Null marks a pair with no route. Failed requests are not cached. */
const sessionCache = new Map<string, LegTime | null>()

export function cachedGoogleTimes(keys: string[]): Record<string, LegTime> {
    const times: Record<string, LegTime> = {}
    for (const key of keys) {
        const time = sessionCache.get(key)
        if (time) {
            times[key] = time
        }
    }
    return times
}

export function clearDriveTimeCache() {
    sessionCache.clear()
}

type ImportLibrary = (name: 'routes') => Promise<unknown>

/** Returns the RouteMatrix class, or null when the Routes library cannot be loaded. */
export async function loadRouteMatrix(
    importLibrary: ImportLibrary | undefined = globalThis.google?.maps?.importLibrary as
        | ImportLibrary
        | undefined,
): Promise<RouteMatrixLike | null> {
    if (!importLibrary) {
        return null
    }

    try {
        const library = (await importLibrary('routes')) as {RouteMatrix?: RouteMatrixLike}
        return typeof library?.RouteMatrix?.computeRouteMatrix === 'function'
            ? library.RouteMatrix
            : null
    } catch {
        return null
    }
}

/**
 * Fetch uncached leg times, one origin-destination pair per request.
 * Resolves false when a request fails and skips the remaining batches.
 */
export async function fetchGoogleLegTimes(
    routeMatrix: RouteMatrixLike,
    legs: LegRequest[],
): Promise<boolean> {
    const pending = legs.filter(
        (leg, index) =>
            !sessionCache.has(leg.key) &&
            legs.findIndex((other) => other.key === leg.key) === index,
    )
    let unavailable = false
    const askGoogle = async (leg: LegRequest) => {
        try {
            const {matrix} = await routeMatrix.computeRouteMatrix({
                origins: [leg.from],
                destinations: [leg.to],
                travelMode: 'DRIVING',
                fields: ROUTE_MATRIX_FIELDS,
            })
            const item = matrix?.rows?.[0]?.items?.[0]
            const answered =
                item &&
                !item.error &&
                item.condition === 'ROUTE_EXISTS' &&
                typeof item.durationMillis === 'number' &&
                typeof item.distanceMeters === 'number'
            sessionCache.set(
                leg.key,
                answered
                    ? {
                          miles: item.distanceMeters / METERS_PER_MILE,
                          minutes: item.durationMillis / 60_000,
                          method: 'google',
                      }
                    : null,
            )
        } catch {
            unavailable = true
        }
    }
    for (let start = 0; start < pending.length && !unavailable; start += CONCURRENT_REQUESTS) {
        await Promise.all(pending.slice(start, start + CONCURRENT_REQUESTS).map(askGoogle))
    }
    return !unavailable
}

/** Cached Google leg times keyed by leg key. Empty when disabled or the library is unavailable. */
export function useGoogleLegTimes(legs: LegRequest[], enabled: boolean): Record<string, LegTime> {
    const keys = legs.map((leg) => leg.key)
    const query = useQuery({
        queryKey: dispatchKeys.driveTimes(keys),
        queryFn: async () => {
            const routeMatrix = await loadRouteMatrix()
            if (routeMatrix && !(await fetchGoogleLegTimes(routeMatrix, legs))) {
                throw new Error('Drive time request failed')
            }
            return cachedGoogleTimes(keys)
        },
        enabled: enabled && legs.length > 0,
        staleTime: Infinity,
    })

    if (!enabled) {
        return {}
    }

    return query.data ?? cachedGoogleTimes(keys)
}
